// src/components/Query/QueryForm.tsx
//
// The query form and everything that makes it work: validation, the email call,
// the loading state, the error state and the success message.
//
// This is the single implementation of that logic. It is rendered in two places
// — inside QueryModal, and on the /query route that is kept for bookmarks — and
// both get this component, not a copy. Nothing here knows whether it is being
// displayed as a window or as a page.
//
// The one thing that used to live in the page wrapper was the post-success
// redirect to "/". That has been replaced by the optional onSuccess callback, so
// the caller decides what happens after a send: a modal closes itself, a page
// stays where it is. Neither is forced back to the home page.
import { useId } from 'react';
import { FaPaperPlane, FaTimesCircle } from 'react-icons/fa';

import Loader from '../Loader';
import SuccessModal from '../SuccessModal';
import { sendQueryEmail } from '../../services/emailService';
import { useEmailForm, validateQueryForm } from '../../hooks/useEmailForm';
import { RequiredMark } from '../../styles/formStyles';
import {
  ErrorMessage,
  Field,
  Form,
  Input,
  Label,
  Lead,
  Row,
  SubmitButton,
  TextArea,
} from './QueryForm.styles';

export interface QueryFormProps {
  /**
   * Fired once the success message has been shown and has run its course, i.e.
   * after SuccessModal auto-closes. QueryModal passes the modal's onClose here
   * so the window shuts only after the visitor has seen the confirmation. When
   * omitted the success message simply dismisses itself and the form stays.
   */
  onSuccess?: () => void;
  /** How long the success message stays up, in milliseconds. */
  successDuration?: number;
}

const QueryForm = ({ onSuccess, successDuration = 4000 }: QueryFormProps) => {
  /**
   * Field ids are generated rather than hardcoded. The form can legitimately be
   * mounted more than once on a page — the /query route renders it, and a modal
   * opened from that page would render it again — and duplicated DOM ids break
   * the label/control association and any document.getElementById lookup.
   *
   * The name attributes are a different matter and are load-bearing: the
   * FormData keys they produce are what validateQueryForm and sendQueryEmail
   * read, so they are fixed strings and must not be changed.
   */
  const fieldId = useId();
  const id = (field: string) => `${fieldId}-${field}`;

  const { isLoading, errorMessage, handleSubmit, showSuccess, closeSuccessModal } = useEmailForm({
    onSubmit: async (payload: any) => {
      // Validate form data
      const errors = validateQueryForm(payload);
      if (Object.keys(errors).length > 0) {
        throw new Error(Object.values(errors).join(', '));
      }

      // Send email
      await sendQueryEmail(
        payload.fullName,
        payload.designation || '',
        payload.emailAddress,
        payload.phoneNumber,
        payload.querry
      );
    },
  });

  return (
    <>
      {/* The shared Loader is a full-screen overlay at z-index 9999, so it sits
          above the window while a send is in flight. That is the intended
          layering: it also blocks a second submit from being started. */}
      <Loader isVisible={isLoading && !showSuccess} message="Sending your query..." />

      <SuccessModal
        isVisible={showSuccess}
        onClose={closeSuccessModal}
        onAutoClose={onSuccess}
        autoCloseDuration={successDuration}
      />

      <Lead>
        Have a question or want to work together? Fill this in and I&apos;ll get back to you as
        soon as possible.
      </Lead>

      {errorMessage && (
        <ErrorMessage role="alert">
          <FaTimesCircle aria-hidden="true" />
          <span>{errorMessage}</span>
        </ErrorMessage>
      )}

      <Form onSubmit={handleSubmit} noValidate={false}>
        <Row>
          <Field>
            <Label htmlFor={id('fullName')}>
              Full Name
              <RequiredMark>*</RequiredMark>
            </Label>
            <Input
              id={id('fullName')}
              name="fullName"
              type="text"
              placeholder="John Doe"
              autoComplete="name"
              required
              disabled={isLoading}
              /* Focus lands here when the window opens, rather than on the
                 close control, because this is what the visitor came to do. */
              data-modal-autofocus=""
            />
          </Field>
          <Field>
            <Label htmlFor={id('designation')}>Designation</Label>
            <Input
              id={id('designation')}
              name="designation"
              type="text"
              placeholder="Software Engineer"
              autoComplete="organization-title"
              disabled={isLoading}
            />
          </Field>
        </Row>

        <Row>
          <Field>
            <Label htmlFor={id('emailAddress')}>
              Email Address
              <RequiredMark>*</RequiredMark>
            </Label>
            <Input
              id={id('emailAddress')}
              name="emailAddress"
              type="email"
              placeholder="john@example.com"
              autoComplete="email"
              required
              disabled={isLoading}
            />
          </Field>
          <Field>
            <Label htmlFor={id('phoneNumber')}>
              Phone Number
              <RequiredMark>*</RequiredMark>
            </Label>
            <Input
              id={id('phoneNumber')}
              name="phoneNumber"
              type="tel"
              placeholder="+1 (123) 456-7890"
              autoComplete="tel"
              required
              disabled={isLoading}
            />
          </Field>
        </Row>

        <Field>
          <Label htmlFor={id('querry')}>
            Your Query
            <RequiredMark>*</RequiredMark>
          </Label>
          <TextArea
            id={id('querry')}
            name="querry"
            placeholder="Please describe your query or project details..."
            required
            disabled={isLoading}
          />
        </Field>

        <SubmitButton type="submit" disabled={isLoading}>
          <FaPaperPlane aria-hidden="true" />
          {isLoading ? 'Sending...' : 'Submit Query'}
        </SubmitButton>
      </Form>
    </>
  );
};

export default QueryForm;
